import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { SIRUC_RECORDS } from "@/constants/testIds";
import { PhotoCapture } from "@/components/PhotoCapture";
import SignaturePad from "@/components/SignaturePad";
import { syncManager } from "@/lib/syncManager";
import { toast } from "sonner";
import { feedback } from "@/lib/feedback";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Plus, Truck, X, ArrowLeft, RefreshCw, Trash2, Sparkles, FileDown, ShieldCheck, ChevronDown, Share2 } from "lucide-react";
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';

// ── Reglamento y Declaraciones — texto idéntico al que usa SRIUC ──────────
const REGLAS = [
  "1. No romper el sello hasta que la cortina asignada esté abierta y el almacenista responsable esté presente.",
  "2. No pasar materiales/equipos ajenos a NAF por la cortina.",
  "3. Prohibido brincar rampas y entrar al almacén sin autorización.",
  "4. Prohibidos drogas, armas, agentes biológicos, aerosoles, cámaras de video/foto, pornografía y bebidas alcohólicas.",
  "5. Prohibido dar propinas, premios o incentivos al personal de seguridad/almacén NAF.",
  "6. No menores de edad ni personal ajeno a NAF en el patio de maniobras.",
  "7. Prohibido tirar basura en el patio de maniobras.",
  "8. Velocidad máxima 10 km/h.",
  "9. Vehículos escoltados: entrega de documentación en presencia de la escolta.",
  "10. NAF no se responsabiliza por daños a conductores o vehículos causados por terceros.",
  "11. NAF se reserva el derecho de cobrar daños causados por el conductor o vehículo.",
  "12. Las llaves del tractor se entregan a seguridad durante carga/descarga.",
  "13. Instalar cuñas de seguridad si la sucursal lo requiere.",
  "14. Instalar patines de seguridad si la sucursal lo requiere.",
  "15. No maniobrar dollies al entrar cajas/contenedores a rampas.",
  "16. Usar EPP requerido en el patio de maniobras.",
];

const DECLARACIONES = [
  "1. Declaro NO transportar drogas, agentes biológicos, bioterrorismo, municiones, armas, contrabando ni personas indocumentadas.",
  "2. Declaro estar en condición física adecuada y buen estado de salud.",
  "3. Declaro NO haber consumido alcohol o drogas recientemente y NO estar bajo su influencia.",
  "4. Declaro que al estar en instalaciones NAF he leído, entendido y aceptado plenamente estas instrucciones.",
  "5. Declaro que toda la información proporcionada es verídica y no hay anomalías con el transporte ni conmigo.",
];

const GUARDIA_OPCIONES = ["MARIO AGUILAR", "ADELAIDO SAENZ"];

const EMPTY = {
  // Tipo de unidad
  tipo_unidad: "sencillo", // sencillo | full
  sucursal: "Escobedo",
  direccion: "Av. Expansión #350",

  // Datos de la unidad / chofer
  placas_unidad: "",
  chofer_nombre: "",
  licencia_conductor: "",
  compania_transporte: "",
  numero_tractor: "",

  // Caja 1
  empresa_caja: "",
  numero_caja: "",
  placas_caja: "",
  tipo_sello_entrada: "", // precinto | alta_seguridad
  sello_entrada: "",
  sello_entrada_na: false,

  // Caja 2 (solo si tipo_unidad === 'full')
  empresa_caja_2: "",
  numero_caja_2: "",
  tipo_sello_entrada_2: "",
  sello_entrada_2: "",
  sello_entrada_2_na: false,

  // Escolta
  escolta_presente: false,
  escolta_compania: "",
  escolta_unidad: "",
  escolta_placas: "",

  // Fotografías
  foto_frente_unidad: "",
  foto_atras_caja: "",
  foto_atras_caja_2: "",
  foto_id_chofer: "",

  // Carga y operación
  cortina_asignada: "",
  guardia_caseta_nombre: "",
  condicion_carga: "vacia", // vacia | cargada | consolidada | otra
  descripcion_carga: "",
  orden_compra: false,
  numero_orden_compra: "",
  destino: "",

  // Reglamento + firma del operador
  acepta_reglamento: false,
  firma_chofer: "",
  firma_guardia: "",
};

export default function SirucRecords() {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [guardiaOtro, setGuardiaOtro] = useState(false);
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownloadPDF = async (id) => {
    try {
      setDownloadingId(id);

      const res = await api.get(`/siruc/records/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const fileName = `REPORTE_CONSOLIDADO_${id}.pdf`;

      if (Capacitor.isNativePlatform()) {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          const base64Data = reader.result.split(',')[1];
          const savedFile = await Filesystem.writeFile({
            path: fileName,
            data: base64Data,
            directory: Directory.Cache
          });
          await Share.share({
            title: 'Reporte Consolidado NAF',
            text: `Unidad ${id}`,
            url: savedFile.uri,
          });
        };
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error("Error al procesar reporte");
    } finally {
      setDownloadingId(null);
    }
  };

  const { data: records = [], isLoading } = useQuery({
    queryKey: ["siruc-records"],
    queryFn: () => api.get("/siruc/records").then((r) => r.data),
  });

  const syncMutation = useMutation({
    mutationFn: () => api.post("/sync/sriuc/run").then((r) => r.data),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["siruc-records"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success(`Sincronización exitosa: ${res.processed ?? res.vehicle_records ?? 0} registros procesados.`);
    },
    onError: (e) => toast.error(apiError(e, "Error al sincronizar con SRIUC")),
  });

  const createMutation = useMutation({
    mutationFn: (body) => api.post("/siruc/records", body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["siruc-records"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Registro creado correctamente");
      feedback.success();
      setShowForm(false);
      setForm(EMPTY);
      setGuardiaOtro(false);
    },
    onError: (e) => { toast.error(apiError(e, "Error al crear registro")); feedback.error(); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/siruc/records/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["siruc-records"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Registro eliminado");
    },
    onError: (e) => toast.error(apiError(e, "Error al eliminar registro")),
  });

  // Normaliza las llaves del JSON que regresa la IA a snake_case en minúsculas,
  // así evitamos discrepancias de mayúsculas/formato que rompan el auto-llenado.
  const normalizeOcrKeys = (data) => {
    const out = {};
    Object.entries(data || {}).forEach(([k, v]) => {
      out[String(k).trim().toLowerCase()] = v;
    });
    return out;
  };

  const ocrMutation = useMutation({
    mutationFn: (body) => api.post("/ocr/analyze", body).then(r => r.data),
    onSuccess: (raw) => {
      const data = normalizeOcrKeys(raw);
      setForm(prev => ({
        ...prev,
        placas_unidad: data.placas_unidad ? String(data.placas_unidad).toUpperCase() : prev.placas_unidad,
        chofer_nombre: data.chofer_nombre ? String(data.chofer_nombre).toUpperCase() : prev.chofer_nombre,
        licencia_conductor: data.licencia_conductor || prev.licencia_conductor,
        compania_transporte: data.compania_transporte ? String(data.compania_transporte).toUpperCase() : prev.compania_transporte,
        numero_tractor: data.numero_tractor ? String(data.numero_tractor).toUpperCase() : prev.numero_tractor,
        empresa_caja: data.empresa_caja ? String(data.empresa_caja).toUpperCase() : prev.empresa_caja,
        numero_caja: data.numero_caja ? String(data.numero_caja).toUpperCase() : prev.numero_caja,
        placas_caja: data.placas_caja ? String(data.placas_caja).toUpperCase() : prev.placas_caja,
        sello_entrada: data.sello_entrada ? String(data.sello_entrada).toUpperCase() : prev.sello_entrada,
        tipo_sello_entrada: data.sello_entrada ? (prev.tipo_sello_entrada || "precinto") : prev.tipo_sello_entrada,
        condicion_carga: data.condicion_carga || prev.condicion_carga,
        destino: data.destino ? String(data.destino).toUpperCase() : prev.destino,
        descripcion_carga: data.descripcion_carga ? String(data.descripcion_carga).toUpperCase() : prev.descripcion_carga,
      }));
      toast.success("Formulario físico procesado con éxito");
    },
    onError: (e) => toast.error(apiError(e, "Error al analizar el documento")),
  });

  const handleScan = (img) => {
    ocrMutation.mutate({ image_b64: img, context: "entry" });
  };

  const isFull = form.tipo_unidad === "full";

  const canSubmit =
    form.placas_unidad.trim() &&
    form.chofer_nombre.trim() &&
    form.guardia_caseta_nombre.trim() &&
    form.acepta_reglamento &&
    form.firma_chofer &&
    (!isFull || (form.numero_caja_2.trim() && form.foto_atras_caja_2));

  const submit = (e) => {
    e.preventDefault();
    if (!form.placas_unidad || !form.chofer_nombre) {
      toast.error("Placas y chofer son obligatorios");
      return;
    }
    if (!form.acepta_reglamento || !form.firma_chofer) {
      toast.error("El operador debe aceptar el reglamento y firmar antes de continuar");
      return;
    }

    const body = {
      ...form,
      placas_unidad: form.placas_unidad.trim().toUpperCase(),
      chofer_nombre: form.chofer_nombre.trim().toUpperCase(),
      compania_transporte: form.compania_transporte.trim().toUpperCase(),
      numero_tractor: form.numero_tractor.trim().toUpperCase(),
      empresa_caja: form.empresa_caja.trim().toUpperCase(),
      numero_caja: form.numero_caja.trim().toUpperCase(),
      placas_caja: form.placas_caja.trim().toUpperCase(),
      sello_entrada: form.sello_entrada_na ? "N/A" : form.sello_entrada.trim().toUpperCase(),
      empresa_caja_2: isFull ? form.empresa_caja_2.trim().toUpperCase() : "",
      numero_caja_2: isFull ? form.numero_caja_2.trim().toUpperCase() : "",
      sello_entrada_2: isFull ? (form.sello_entrada_2_na ? "N/A" : form.sello_entrada_2.trim().toUpperCase()) : "",
      guardia_caseta_nombre: form.guardia_caseta_nombre.trim().toUpperCase(),
      descripcion_carga: form.descripcion_carga.trim().toUpperCase(),
      destino: form.destino.trim().toUpperCase(),
      fecha_entrada: new Date().toISOString(),
    };

    if (syncManager.isOffline()) {
      syncManager.addToQueue("/siruc/records", "POST", body, `Registro ${body.placas_unidad}`);
      setShowForm(false);
      setForm(EMPTY);
      return;
    }

    createMutation.mutate(body);
  };

  if (showForm) {
    return (
      <div className="p-0 sm:p-4 lg:p-8 max-w-4xl mx-auto bg-secondary/30 min-h-screen">
        <div className="sticky top-0 z-20 bg-white border-b border-border p-4 flex items-center justify-between shadow-sm">
          <button
            onClick={() => setShowForm(false)}
            className="flex items-center gap-2 text-navy font-bold uppercase text-xs"
          >
            <ArrowLeft size={18} /> Volver
          </button>
          <h1 className="font-heading text-lg font-black text-navy uppercase tracking-tight">Registro de Unidad</h1>
          <div className="w-10"></div>
        </div>

        <div className="p-4 space-y-6 pb-24">
          {/* ── AI SCAN ─────────────────────────────────────────────── */}
          <section className="bg-white border border-border p-4 rounded-xl shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-navy text-white rounded-lg flex items-center justify-center">
                <Sparkles size={18} />
              </div>
              <div>
                <h2 className="font-heading font-bold text-navy leading-none">Auto-llenado Inteligente</h2>
                <p className="text-[10px] text-muted-foreground uppercase font-black mt-1">Escanea el documento físico de caseta</p>
              </div>
            </div>

            <PhotoCapture
              single
              label="Escaneo de documento (Cámara o Galería)"
              onChange={(v) => handleScan(v[0])}
              testid="siruc-full-form-scan"
            />
            {ocrMutation.isPending && (
              <div className="mt-3 p-3 bg-navy/5 border border-navy/20 rounded-lg flex items-center gap-3">
                <RefreshCw size={16} className="text-navy animate-spin" />
                <span className="text-xs font-bold text-navy animate-pulse">Analizando documento físico...</span>
              </div>
            )}
          </section>

          <form onSubmit={submit} className="space-y-6">
            {/* ── TIPO DE UNIDAD ────────────────────────────────────── */}
            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Tipo de Unidad</p>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  {["sencillo", "full"].map((opt) => (
                    <button
                      key={opt}
                      type="button"
                      data-testid={opt === "sencillo" ? SIRUC_RECORDS.formTipoUnidad : undefined}
                      onClick={() => setForm({ ...form, tipo_unidad: opt })}
                      className={`h-12 rounded-lg font-black uppercase text-xs tracking-widest border-2 transition-colors ${
                        form.tipo_unidad === opt ? "bg-navy text-white border-navy" : "bg-secondary/20 text-navy/60 border-transparent"
                      }`}
                    >
                      {opt === "sencillo" ? "Sencillo (1 caja)" : "Full (2 cajas)"}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ── DATOS DE LA UNIDAD ──────────────────────────────────── */}
            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Datos de la Unidad</p>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Placas *">
                    <input
                      data-testid={SIRUC_RECORDS.formPlacas}
                      value={form.placas_unidad}
                      onChange={(e) => setForm({ ...form, placas_unidad: e.target.value })}
                      className="input h-12 bg-secondary/20 border-none uppercase font-bold"
                      placeholder="ABC-123"
                      required
                    />
                  </Field>
                  <Field label="Número de Tractor">
                    <input
                      value={form.numero_tractor}
                      onChange={(e) => setForm({ ...form, numero_tractor: e.target.value })}
                      className="input h-12 bg-secondary/20 border-none font-bold"
                      placeholder="T-45"
                    />
                  </Field>
                </div>
                <Field label="Compañía Transportista">
                  <input
                    data-testid={SIRUC_RECORDS.formCompania}
                    value={form.compania_transporte}
                    onChange={(e) => setForm({ ...form, compania_transporte: e.target.value })}
                    className="input h-12 bg-secondary/20 border-none"
                    placeholder="Nombre de la empresa"
                  />
                </Field>
              </div>
            </div>

            {/* ── DATOS DEL CHOFER ────────────────────────────────────── */}
            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Datos del Chofer</p>
              </div>
              <div className="p-4 space-y-4">
                <Field label="Nombre del chofer *">
                  <input
                    data-testid={SIRUC_RECORDS.formChofer}
                    value={form.chofer_nombre}
                    onChange={(e) => setForm({ ...form, chofer_nombre: e.target.value })}
                    className="input h-12 bg-secondary/20 border-none"
                    placeholder="Nombre completo"
                    required
                  />
                </Field>
                <Field label="Licencia de conducir">
                  <input
                    data-testid={SIRUC_RECORDS.formLicencia}
                    value={form.licencia_conductor}
                    onChange={(e) => setForm({ ...form, licencia_conductor: e.target.value })}
                    className="input h-12 bg-secondary/20 border-none"
                    placeholder="Número de licencia"
                  />
                </Field>
                <PhotoCapture
                  label="Foto ID Chofer *"
                  single
                  value={form.foto_id_chofer ? [form.foto_id_chofer] : []}
                  onChange={(v) => setForm({ ...form, foto_id_chofer: v[0] || "" })}
                  testid="siruc-foto-id"
                />
              </div>
            </div>

            {/* ── CAJA 1 ──────────────────────────────────────────────── */}
            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Caja 1</p>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Empresa de la Caja">
                    <input
                      value={form.empresa_caja}
                      onChange={(e) => setForm({ ...form, empresa_caja: e.target.value })}
                      className="input h-12 bg-secondary/20 border-none"
                    />
                  </Field>
                  <Field label="Número de Caja">
                    <input
                      value={form.numero_caja}
                      onChange={(e) => setForm({ ...form, numero_caja: e.target.value })}
                      className="input h-12 bg-secondary/20 border-none"
                    />
                  </Field>
                </div>
                <Field label="Placas de Caja">
                  <input
                    value={form.placas_caja}
                    onChange={(e) => setForm({ ...form, placas_caja: e.target.value })}
                    className="input h-12 bg-secondary/20 border-none"
                  />
                </Field>

                <Field label="Sello de Entrada">
                  <div className="flex gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, tipo_sello_entrada: "precinto" })}
                      className={`flex-1 h-10 rounded-lg text-[11px] font-black uppercase border-2 ${form.tipo_sello_entrada === "precinto" ? "bg-navy text-white border-navy" : "bg-secondary/20 text-navy/60 border-transparent"}`}
                    >
                      Precinto
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, tipo_sello_entrada: "alta_seguridad" })}
                      className={`flex-1 h-10 rounded-lg text-[11px] font-black uppercase border-2 ${form.tipo_sello_entrada === "alta_seguridad" ? "bg-navy text-white border-navy" : "bg-secondary/20 text-navy/60 border-transparent"}`}
                    >
                      Alta Seguridad
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid={SIRUC_RECORDS.formSelloEntrada}
                      value={form.sello_entrada}
                      disabled={form.sello_entrada_na}
                      onChange={(e) => setForm({ ...form, sello_entrada: e.target.value })}
                      className="input h-12 bg-secondary/20 border-none font-bold flex-1 disabled:opacity-40"
                      placeholder="Número de sello"
                    />
                    <label className="flex items-center gap-1.5 text-[10px] font-black uppercase text-navy/60 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={form.sello_entrada_na}
                        onChange={(e) => setForm({ ...form, sello_entrada_na: e.target.checked, sello_entrada: "" })}
                      />
                      N/A
                    </label>
                  </div>
                </Field>
              </div>
            </div>

            {/* ── CAJA 2 (solo Full) ──────────────────────────────────── */}
            {isFull && (
              <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
                <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                  <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Caja 2</p>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Empresa de la Caja 2">
                      <input
                        value={form.empresa_caja_2}
                        onChange={(e) => setForm({ ...form, empresa_caja_2: e.target.value })}
                        className="input h-12 bg-secondary/20 border-none"
                      />
                    </Field>
                    <Field label="Número de Caja 2 *">
                      <input
                        value={form.numero_caja_2}
                        onChange={(e) => setForm({ ...form, numero_caja_2: e.target.value })}
                        className="input h-12 bg-secondary/20 border-none"
                      />
                    </Field>
                  </div>
                  <Field label="Sello de Entrada 2">
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, tipo_sello_entrada_2: "precinto" })}
                        className={`flex-1 h-10 rounded-lg text-[11px] font-black uppercase border-2 ${form.tipo_sello_entrada_2 === "precinto" ? "bg-navy text-white border-navy" : "bg-secondary/20 text-navy/60 border-transparent"}`}
                      >
                        Precinto
                      </button>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, tipo_sello_entrada_2: "alta_seguridad" })}
                        className={`flex-1 h-10 rounded-lg text-[11px] font-black uppercase border-2 ${form.tipo_sello_entrada_2 === "alta_seguridad" ? "bg-navy text-white border-navy" : "bg-secondary/20 text-navy/60 border-transparent"}`}
                      >
                        Alta Seguridad
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        value={form.sello_entrada_2}
                        disabled={form.sello_entrada_2_na}
                        onChange={(e) => setForm({ ...form, sello_entrada_2: e.target.value })}
                        className="input h-12 bg-secondary/20 border-none font-bold flex-1 disabled:opacity-40"
                        placeholder="Número de sello"
                      />
                      <label className="flex items-center gap-1.5 text-[10px] font-black uppercase text-navy/60 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={form.sello_entrada_2_na}
                          onChange={(e) => setForm({ ...form, sello_entrada_2_na: e.target.checked, sello_entrada_2: "" })}
                        />
                        N/A
                      </label>
                    </div>
                  </Field>
                  <PhotoCapture
                    label="Foto Trasera Caja 2 *"
                    single
                    value={form.foto_atras_caja_2 ? [form.foto_atras_caja_2] : []}
                    onChange={(v) => setForm({ ...form, foto_atras_caja_2: v[0] || "" })}
                    testid="siruc-foto-atras-2"
                  />
                </div>
              </div>
            )}

            {/* ── ESCOLTA ─────────────────────────────────────────────── */}
            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-secondary/50 px-4 py-2 border-b border-border flex items-center justify-between">
                <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Escolta</p>
                <label className="flex items-center gap-2 text-[10px] font-black uppercase text-navy">
                  <input
                    type="checkbox"
                    checked={form.escolta_presente}
                    onChange={(e) => setForm({ ...form, escolta_presente: e.target.checked })}
                  />
                  Presente
                </label>
              </div>
              {form.escolta_presente && (
                <div className="p-4 space-y-4">
                  <Field label="Compañía de la Escolta">
                    <input
                      value={form.escolta_compania}
                      onChange={(e) => setForm({ ...form, escolta_compania: e.target.value })}
                      className="input h-12 bg-secondary/20 border-none"
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Unidad">
                      <input
                        value={form.escolta_unidad}
                        onChange={(e) => setForm({ ...form, escolta_unidad: e.target.value })}
                        className="input h-12 bg-secondary/20 border-none"
                      />
                    </Field>
                    <Field label="Placas">
                      <input
                        value={form.escolta_placas}
                        onChange={(e) => setForm({ ...form, escolta_placas: e.target.value })}
                        className="input h-12 bg-secondary/20 border-none"
                      />
                    </Field>
                  </div>
                </div>
              )}
            </div>

            {/* ── FOTOGRAFÍAS DE UNIDAD ───────────────────────────────── */}
            <div className="bg-white border border-border rounded-xl p-4 shadow-sm space-y-4">
              <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Fotografías de Unidad</p>
              <PhotoCapture
                label="Foto Frente Unidad *"
                single
                value={form.foto_frente_unidad ? [form.foto_frente_unidad] : []}
                onChange={(v) => setForm({ ...form, foto_frente_unidad: v[0] || "" })}
                testid="siruc-foto-frente"
              />
              <PhotoCapture
                label="Foto Atrás Caja 1 *"
                single
                value={form.foto_atras_caja ? [form.foto_atras_caja] : []}
                onChange={(v) => setForm({ ...form, foto_atras_caja: v[0] || "" })}
                testid="siruc-foto-atras"
              />
            </div>

            {/* ── CARGA Y OPERACIÓN ───────────────────────────────────── */}
            <div className="bg-white border border-border rounded-xl overflow-hidden shadow-sm">
              <div className="bg-secondary/50 px-4 py-2 border-b border-border">
                <p className="text-[10px] font-black text-navy/60 uppercase tracking-widest">Carga y Operación</p>
              </div>
              <div className="p-4 space-y-4">
                <Field label="Cortina Asignada">
                  <input
                    value={form.cortina_asignada}
                    onChange={(e) => setForm({ ...form, cortina_asignada: e.target.value })}
                    className="input h-12 bg-secondary/20 border-none"
                    placeholder="Ej. 12"
                  />
                </Field>

                <Field label="Guardia de Caseta *">
                  {!guardiaOtro ? (
                    <select
                      data-testid={SIRUC_RECORDS.formGuardia}
                      value={GUARDIA_OPCIONES.includes(form.guardia_caseta_nombre) ? form.guardia_caseta_nombre : ""}
                      onChange={(e) => {
                        if (e.target.value === "__otro__") { setGuardiaOtro(true); setForm({ ...form, guardia_caseta_nombre: "" }); }
                        else setForm({ ...form, guardia_caseta_nombre: e.target.value });
                      }}
                      className="input h-12 bg-secondary/20 border-none"
                    >
                      <option value="" disabled>Selecciona...</option>
                      {GUARDIA_OPCIONES.map((g) => <option key={g} value={g}>{g}</option>)}
                      <option value="__otro__">Otro...</option>
                    </select>
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        autoFocus
                        value={form.guardia_caseta_nombre}
                        onChange={(e) => setForm({ ...form, guardia_caseta_nombre: e.target.value })}
                        className="input h-12 bg-secondary/20 border-none flex-1"
                        placeholder="Nombre del guardia"
                      />
                      <button type="button" onClick={() => setGuardiaOtro(false)} className="text-[10px] font-black uppercase text-navy/60">
                        Lista
                      </button>
                    </div>
                  )}
                </Field>

                <Field label="Condición de la Carga *">
                  <select
                    value={form.condicion_carga}
                    onChange={(e) => setForm({ ...form, condicion_carga: e.target.value })}
                    className="input h-12 bg-secondary/20 border-none"
                  >
                    <option value="vacia">Vacía (viene a cargar)</option>
                    <option value="cargada">Cargada (viene a descargar)</option>
                    <option value="consolidada">Consolidada</option>
                    <option value="otra">Otra</option>
                  </select>
                </Field>

                <Field label="Descripción de la Carga">
                  <input
                    data-testid={SIRUC_RECORDS.formDescripcion}
                    value={form.descripcion_carga}
                    onChange={(e) => setForm({ ...form, descripcion_carga: e.target.value })}
                    className="input h-12 bg-secondary/20 border-none"
                  />
                </Field>

                <div>
                  <label className="flex items-center gap-2 text-xs font-bold text-navy mb-2">
                    <input
                      type="checkbox"
                      checked={form.orden_compra}
                      onChange={(e) => setForm({ ...form, orden_compra: e.target.checked })}
                    />
                    Tiene orden de compra
                  </label>
                  {form.orden_compra && (
                    <input
                      value={form.numero_orden_compra}
                      onChange={(e) => setForm({ ...form, numero_orden_compra: e.target.value })}
                      className="input h-12 bg-secondary/20 border-none"
                      placeholder="Número de orden de compra"
                    />
                  )}
                </div>

                <Field label="Destino">
                  <input
                    data-testid={SIRUC_RECORDS.formDestino}
                    value={form.destino}
                    onChange={(e) => setForm({ ...form, destino: e.target.value })}
                    className="input h-12 bg-secondary/20 border-none"
                  />
                </Field>
              </div>
            </div>

            {/* ── REGLAMENTO, DECLARACIONES Y FIRMA DEL OPERADOR ─────── */}
            <div className="bg-white border-2 border-navy rounded-xl overflow-hidden shadow-sm">
              <div className="bg-navy px-4 py-3 flex items-center gap-2">
                <ShieldCheck size={18} className="text-white" />
                <p className="text-xs font-black text-white uppercase tracking-widest">Reglamento y Seguridad</p>
              </div>
              <div className="p-4 space-y-4">
                <div className="max-h-56 overflow-y-auto bg-secondary/20 rounded-lg p-3 space-y-1.5 text-[11px] text-navy/80 leading-snug">
                  {REGLAS.map((r) => <p key={r}>{r}</p>)}
                </div>

                <p className="text-xs font-black text-navy uppercase tracking-widest pt-2">Declaraciones del Operador</p>
                <div className="max-h-40 overflow-y-auto bg-secondary/20 rounded-lg p-3 space-y-1.5 text-[11px] text-navy/80 leading-snug">
                  {DECLARACIONES.map((d) => <p key={d}>{d}</p>)}
                </div>

                <label className="flex items-start gap-3 pt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 w-5 h-5"
                    checked={form.acepta_reglamento}
                    onChange={(e) => setForm({ ...form, acepta_reglamento: e.target.checked })}
                  />
                  <span className="text-xs font-bold text-navy">
                    He leído, entendido y acepto el reglamento de seguridad y declaro que la información y declaraciones anteriores son verídicas. *
                  </span>
                </label>

                {form.acepta_reglamento && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <SignaturePad
                      label="FIRMA DEL CHOFER (ACEPTACIÓN DE REGLAMENTO) *"
                      value={form.firma_chofer}
                      onChange={(v) => setForm({ ...form, firma_chofer: v })}
                      testid="siruc-firma-chofer-ent"
                    />
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              data-testid={SIRUC_RECORDS.formSubmit}
              disabled={createMutation.isPending || !canSubmit}
              className="w-full h-14 bg-navy text-white font-black uppercase tracking-widest rounded-xl shadow-lg active:scale-95 transition-all disabled:opacity-50"
            >
              {createMutation.isPending ? "Procesando..." : !canSubmit ? "Completa los campos obligatorios (*)" : "Registrar Ingreso"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight text-navy">SIRUC · Registros</h1>
          <p className="text-sm text-muted-foreground mt-1">Control de entrada y salida de unidades.</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <button
              onClick={() => syncMutation.mutate()}
              disabled={syncMutation.isPending}
              className="flex items-center gap-2 h-11 px-4 border border-navy text-navy font-semibold hover:bg-navy/5 disabled:opacity-50"
            >
              <RefreshCw size={18} className={syncMutation.isPending ? "animate-spin" : ""} />
              {syncMutation.isPending ? "Sincronizando..." : "Sincronizar SRIUC"}
            </button>
          )}
          <button
            onClick={() => setShowForm(true)}
            data-testid={SIRUC_RECORDS.newButton}
            className="flex items-center gap-2 h-11 px-4 bg-navy text-white font-semibold hover:bg-navy-hover"
          >
            <Plus size={18} /> Nuevo registro
          </button>
        </div>
      </header>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : records.length === 0 ? (
        <EmptyState
          icon={Truck}
          title="Sin registros"
          message="Crea el primer registro de entrada de unidad."
          action={
            <button
              onClick={() => setShowForm(true)}
              data-testid={SIRUC_RECORDS.newButton}
              className="h-11 px-4 bg-navy text-white font-semibold hover:bg-navy-hover"
            >
              + Nuevo registro
            </button>
          }
        />
      ) : (
        <div className="bg-white border border-border overflow-hidden">
          {/* Mobile View: Cards */}
          <div className="block md:hidden divide-y divide-border">
            {records.map((r) => (
              <div
                key={r.id}
                className="p-4 active:bg-secondary flex flex-col gap-2"
                onClick={() => navigate(`/siruc/records/${r.id}`)}
              >
                <div className="flex justify-between items-start">
                  <span className="text-base font-black text-navy tracking-tight">{r.entry?.placas_unidad || "S/P"}</span>
                  <StatusBadge status={r.status} />
                </div>
                <div className="text-sm">
                  <p className="font-bold text-navy/80 uppercase">{r.entry?.chofer_nombre || "Sin chofer"}</p>
                  <p className="text-xs text-muted-foreground truncate uppercase">{r.entry?.compania_transporte || "Sin compañía"}</p>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-muted-foreground uppercase font-black tracking-widest">
                      {new Date(r.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                    {r.status === "salida" && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadPDF(r.id); }}
                        disabled={downloadingId === r.id}
                        className="p-1 text-navy hover:bg-navy/10 rounded"
                      >
                        <FileDown size={14} className={downloadingId === r.id ? "animate-spin" : ""} />
                      </button>
                    )}
                  </div>
                  <span className="text-xs font-black text-navy">DETALLE →</span>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop View: Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary border-b border-border">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Placas</th>
                  <th className="px-4 py-3 font-medium">Chofer</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Compañía</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Destino</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Creado</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody data-testid={SIRUC_RECORDS.list} className="divide-y divide-border">
                {records.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={SIRUC_RECORDS.row}
                    className="hover:bg-secondary/50 cursor-pointer"
                    onClick={() => navigate(`/siruc/records/${r.id}`)}
                  >
                    <td className="px-4 py-3 font-medium text-navy">{r.entry?.placas_unidad || "—"}</td>
                    <td className="px-4 py-3">{r.entry?.chofer_nombre || "—"}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{r.entry?.compania_transporte || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{r.entry?.destino || "—"}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground text-xs">
                      {new Date(r.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
                    </td>
                    <td className="px-4 py-3 text-right flex items-center justify-end gap-2">
                      {r.status === "salida" && (
                        <button
                          onClick={(ev) => { ev.stopPropagation(); handleDownloadPDF(r.id); }}
                          disabled={downloadingId === r.id}
                          title="Descargar Reporte Consolidado"
                          className="p-1.5 text-navy hover:bg-navy/10 rounded"
                        >
                          <FileDown size={16} className={downloadingId === r.id ? "animate-spin" : ""} />
                        </button>
                      )}
                      <span className="text-navy text-xs hover:underline" onClick={(ev) => { ev.stopPropagation(); navigate(`/siruc/records/${r.id}`); }}>Ver →</span>
                      {user?.role === "admin" && (
                        <button
                          onClick={(ev) => {
                            ev.stopPropagation();
                            if (confirm("¿Eliminar este registro y todas sus bitácoras asociadas?")) {
                              deleteMutation.mutate(r.id);
                            }
                          }}
                          className="p-1.5 text-critical hover:bg-critical/10 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-navy">{label}</label>
      {children}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    entrada: { label: "Entrada", cls: "bg-navy/10 text-navy" },
    inspeccionado: { label: "Inspeccionado", cls: "bg-warn/10 text-warn" },
    salida: { label: "Salida", cls: "bg-success/10 text-success" },
  };
  const s = map[status] || { label: status, cls: "bg-secondary text-muted-foreground" };
  return <span className={`inline-block px-2 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

function EmptyState({ icon: Icon, title, message, action }) {
  return (
    <div className="bg-white border border-border p-12 flex flex-col items-center text-center">
      <span className="w-14 h-14 bg-secondary text-navy flex items-center justify-center mb-4">
        <Icon size={28} />
      </span>
      <h3 className="font-heading text-xl font-bold text-navy">{title}</h3>
      <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-sm">{message}</p>
      {action}
    </div>
  );
}
